// cohorts/manage types: the settings form and the changes it sends.
// Request shapes of `PATCH /cohorts/{id}/` and the lifecycle calls (cohorts-v1.md).
import { z } from "zod";
import type { Cohort } from "../../_shared/types";

/** A number field left empty gives `NaN`: allowed for the two optional settings. */
const OptionalCount = z.union([
  z
    .number()
    .int("settings.errors.count-invalid")
    .min(0, "settings.errors.count-invalid"),
  z.nan(),
]);

/**
 * What the person edits on the cohort settings page. Messages are i18n keys in the `cohorts`
 * namespace. The code is shown read-only and is not a field.
 */
export const CohortSettingsFormSchema = z
  .object({
    name: z.string().trim().min(1, "create.errors.name-required"),
    places: z
      .number("create.errors.places-invalid")
      .int("create.errors.places-invalid")
      .min(1, "create.errors.places-invalid"),
    waitlistPlaces: z
      .number("create.errors.waitlist-invalid")
      .int("create.errors.waitlist-invalid")
      .min(0, "create.errors.waitlist-invalid"),
    interviewMarginPercent: OptionalCount,
    responseDelayDays: OptionalCount,
    startDate: z.string().min(1, "create.errors.start-required"),
    endDate: z.string().min(1, "create.errors.end-required"),
    administratorId: z.string(),
  })
  .refine(
    (value) =>
      !value.startDate || !value.endDate || value.endDate > value.startDate,
    { path: ["endDate"], message: "create.errors.end-before-start" }
  );

export type CohortSettingsValues = z.infer<typeof CohortSettingsFormSchema>;

/** The form's starting values, read from the cohort the server sent. */
export function settingsValuesOf(cohort: Cohort): CohortSettingsValues {
  return {
    name: cohort.name,
    places: cohort.places,
    waitlistPlaces: cohort.waitlist_places,
    interviewMarginPercent: cohort.interview_margin_percent ?? Number.NaN,
    responseDelayDays: cohort.response_delay_days ?? Number.NaN,
    startDate: cohort.program_start_date ?? "",
    endDate: cohort.program_end_date ?? "",
    administratorId: cohort.responsible_administrator?.id ?? "",
  };
}

/** The cohort fields that changed, in the API's names. Empty when nothing changed. */
export interface CohortChanges {
  name?: string;
  places?: number;
  waitlist_places?: number;
  interview_margin_percent?: number | null;
  response_delay_days?: number | null;
  program_start_date?: string;
  program_end_date?: string;
}

const orNull = (value: number): number | null =>
  Number.isNaN(value) ? null : value;

/**
 * What to send: only the fields that differ from the cohort. A cleared optional number is sent as
 * `null` (the API allows it). Pure, so it is tested in plain Node.
 */
export function diffSettings(
  cohort: Cohort,
  values: CohortSettingsValues
): CohortChanges {
  const changes: CohortChanges = {};
  if (values.name.trim() !== cohort.name) changes.name = values.name.trim();
  if (values.places !== cohort.places) changes.places = values.places;
  if (values.waitlistPlaces !== cohort.waitlist_places) {
    changes.waitlist_places = values.waitlistPlaces;
  }
  const margin = orNull(values.interviewMarginPercent);
  if (margin !== (cohort.interview_margin_percent ?? null)) {
    changes.interview_margin_percent = margin;
  }
  const delay = orNull(values.responseDelayDays);
  if (delay !== (cohort.response_delay_days ?? null)) {
    changes.response_delay_days = delay;
  }
  if (values.startDate !== (cohort.program_start_date ?? "")) {
    changes.program_start_date = values.startDate;
  }
  if (values.endDate !== (cohort.program_end_date ?? "")) {
    changes.program_end_date = values.endDate;
  }
  return changes;
}

/** Whether the person picked another responsible Administrator than the saved one. */
export function administratorChanged(
  cohort: Cohort,
  values: CohortSettingsValues
): boolean {
  return (
    values.administratorId !== "" &&
    values.administratorId !== (cohort.responsible_administrator?.id ?? "")
  );
}
